import { Box, Typography } from '@mui/material';

import { Timestamp } from '@bufbuild/protobuf';
import {
  AssessInfoCard,
  ClickerPoupSingleButton,
  EarlyHomeworkPopup,
  FullWidthSectionList,
  IStyles,
  InfoDisplayCard,
  PrimaryButton,
  ShimmerActiveHwCard,
  deserify,
  formatSecondsToDateTimeString,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  remainingTimeInHours,
  roundNumberTo2Decimal,
  setLocalStorage,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import {
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import {
  AssessmentEnum,
  AssignmentConfigEnum,
  ModeOfConductionEnum,
  TaskStudentAttemptStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  HOME,
  HOMEWORK_STUDENT_DASHBOARD,
} from '../../../routeHandling/RoutesNomenclature';
import { onHomeworkCardClick } from '../../../utils/homework';
import { interactionEvent } from '../../Auth/Login/login_logout.events';
import {
  setHomeHomeworkList,
  setToastInfo,
} from '../../Home/reducer/homeDashboard.slice';
import { setHomeworkEndPath } from '../reducer/homework.slice';
import { aiHomeworkCloseEvent } from '../utils_homework/homework.events';
import { HomeworkRecommendations } from './component/HomeworkRecommendations';
import { HwSubmittedHeadings } from './component/HwSubmittedHeadings';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { checkFeatureData } from '../utils_homework/homeworkUtil';
import { StudentAssessmentTask } from '@protos/learning_management/lms.assessment.student.apis_pb';
import SectionListSckeleton from '../../Home/shimmer';

const styles: IStyles = {
  root: {
    // boxSizing: 'border-box',
  },

  headingAndButtonBox: {
    width: { md: '50%', lg: pxTovW(400) },
    paddingRight: { md: pxTovW(100) },
    margin: 'auto',
  },

  buttonBox: {
    width: { xs: pxToRem(294), md: 'max-content' },
    margin: 'auto',
    mb: { xs: pxToRem(20), md: pxTovW(20) },
  },

  sectionListBox: {
    p: { xs: `0`, md: pxToRem(20), lg: `0 ${pxTovW(210)}` },
  },
};
type Props = {
  homeworkFlow?: boolean;
};
export const HwSubmitted = ({ homeworkFlow }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isIpadOnly } = useGlobalContext();
  const { isOffline } = useDownloadContext();
  const pathSegments = location.pathname.split('/');
  const [startTime, setStartTime] = useState<Timestamp | undefined>(undefined);
  const [assessmentType, setAssessmentType] = useState<
    AssessmentEnum | undefined
  >(undefined);
  const homework_id =
    new URLSearchParams(location.search).get('assessmentId') || undefined;
  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const studentId = deserify(
    useAppSelector((state) => state.auth.userInfo?.studentProfileId)
  );
  const selectedSubjectId = deserify(
    useAppSelector((state) => state.home.selected_subject_id)
  );
  // const active_homework_content = deserify(
  //   useAppSelector((state) => state.homework.active_homework_content)
  // );
  // 🔥 SWITCH STATE BASED ON FLOW
  const active_assessment_content = deserify(
    useAppSelector((state) =>
      homeworkFlow
        ? state.homework.active_homework_content
        : state.multisubjectFA.active_assessment_content
    )
  );
  const activeHomeworkContent =
    active_assessment_content && homework_id
      ? active_assessment_content[homework_id]
      : undefined;

  const attempt_result = deserify(
    useAppSelector((state) =>
      homeworkFlow
        ? state.homework.active_homework_attempt_result
        : state.multisubjectFA.active_assessment_attempt_result
    )
  );

  const { homework_end_path } = useAppSelector((state) => state.homework);

  const homeHomeworkList = deserify(
    useAppSelector((state) => state.home.home_homework_list)
  );
  const homeworkDashboardList = deserify(
    useAppSelector((state) => state.homework.home_homework_list)
  );
  const activeHomeworkList =
    homework_end_path === HOME ? homeHomeworkList : homeworkDashboardList;
  const currentResult =
    homework_id && attempt_result ? attempt_result[homework_id] : undefined;

  const { maxScore, responseScore } = currentResult || {};
  const scorePercent = roundNumberTo2Decimal(
    maxScore && responseScore ? (responseScore * 100) / maxScore : 0
  );
  const { setSelectedFunction } = useGlobalContext();
  const [homeworkData, setHomeWorkData] = useState<StudentAssessmentTask[]>([]);
  const { LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [openClickerPopup, setOpenClickerPopup] = useState(false);
  useEffect(() => {
    getHomeworkList();
    (async () => {
      await aiHomeworkCloseEvent({
        homeworkId: Number(homework_id),
        isOffline: isOffline,
      });
    })();

    const redirectToHomeworkDashboard = async () => {
      navigate(homework_end_path ? homework_end_path : HOME);
      dispatch(setHomeworkEndPath(undefined));
    };
    setSelectedFunction(() => redirectToHomeworkDashboard);
    return () => {
      setSelectedFunction(null);
    };
  }, []);
  const getHomeworkList = async () => {
    try {
      if (!studentId) {
        return;
      }
      setLoading(true);
      const response =
        await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getStudentAssessmentList(
          {
            studentId: studentId,
            subjectId: selectedSubjectId,
          }
        );
      if (response) {
        setLoading(false);
        if (response.data) {
          const list = [...(response.data?.allAssessment?.active || [])];
          setHomeWorkData(list);
          return;
        }
      } else {
        setLoading(false);
        setHomeWorkData([]);
      }
    } catch (err) {
      setLoading(false);
      setError(true);
      console.log(err);
    }
  };

  const nextHomeworks = homeworkData
    ?.filter((val) => val.assessmentId !== Number(homework_id))
    .map((val, index) => (
      <AssessInfoCard
        key={'Next_homework_' + index}
        // image={val.assessmentPosterImgUrl}
        // variant={isIpadOnly ? 'large' : 'small'}
        homeworkItem={val}
        modeofConduction={
          val.modeOfConduction ===
          ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
        }
        // iconDetails={[
        //   {
        //     iconName: 'questions',
        //     text: val.noOfQuestions.toString(),
        //     label: 'Questions',
        //   },
        //   {
        //     iconName: 'clock',
        //     text: remainingTimeInHours(val.endDate),
        //     label: 'Remaining',
        //   },
        // ]}
        // rootStyle={{
        //   width: { xs: '100%', md: pxToRem(250), lg: pxTovW(347) },
        //   height: { xs: pxToRem(165), md: pxToRem(100), lg: pxTovW(170) },
        //   maxWidth: { xs: '100%', md: pxToRem(250), lg: pxTovW(347) },
        // }}
        onCardClick={async () => {
          setAssessmentType(val.assessmentType);
          if (
            val.modeOfConduction ===
            ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
          ) {
            setOpenClickerPopup(true);
          } else {
            if (!studentId) {
              return;
            }
            if (val.startDate) {
              if (
                new Date(new Timestamp(val.startDate).toDate()) > new Date()
              ) {
                setStartTime(val.startDate);
                return;
              }
            }
            onHomeworkCardClick(
              dispatch,
              navigate,
              val.assessmentId,
              studentId,
              'active',
              undefined,
              val.taskType,
              val.isMultiSubjectAssessmentFlow
            );

            await interactionEvent({
              url: '',
              context: 'next_homework',
              name: 'HOMEWORK_OPEN',
              pathSegments: pathSegments,
              isOffline: isOffline,
            });
          }
        }}
        proctoring={
          val.assignmentConfigInfo?.assignmentInfo?.find(
            (info) =>
              info.configName ===
              AssignmentConfigEnum.ASSIGNMENT_CONFIG_PROCTORING
          )?.isEnabled
        }
      />
    ));

  const fractionClickHandler = () => {
    //
  };

  const reattemptClickHandler = () => {
    //
    console.log({ activeHomeworkContent, studentId, homework_id });
    if (!studentId || !homework_id) {
      return;
    }
    if (
      new Timestamp(
        activeHomeworkContent?.assessment?.assessmentTargetDate
      ).toDate() < new Date(Date.now())
    ) {
      dispatch(
        setToastInfo({
          label: 'Apologies, Deadline for reattempt has passed.',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    onHomeworkCardClick(
      dispatch,
      navigate,
      Number(homework_id),
      studentId,
      'active',
      undefined,
      activeHomeworkContent?.assessment?.taskType,
      activeHomeworkContent?.assessment?.isMultiSubjectAssessmentFlow
    );
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headingAndButtonBox}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // paddingRight: { md: pxTovW(100) },
          }}
        >
          <HwSubmittedHeadings
            reattemptAllowed={
              checkFeatureData({
                assignmentConfigInfo:
                  activeHomeworkContent?.assessment?.assignmentInfo,
                userInfo: userInfo,
              }).reattempt
            }
            assessmentType={activeHomeworkContent?.assessment?.assessmentType}
            taskType={activeHomeworkContent?.assessment?.taskType}
            scorePercent={Math.round(scorePercent)}
            fractionClickHandler={fractionClickHandler}
            topicName={activeHomeworkContent?.assessment?.moduleName}
            isSubjectiveHw={activeHomeworkContent?.assessmentContent.some((q) =>
              checkSubjectiveQuestion(q)
            )}
          />
        </Box>
        {(activeHomeworkContent?.assessment?.assessmentType ===
          AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT ||
          activeHomeworkContent?.assessment?.assessmentType ===
            AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT) && (
          <Box
            sx={{
              width: 'max-content',
              backgroundColor: '#EFF7FD',
              borderRadius: '10px',
              padding: '10px',
              marginBottom: '10px',
              marginX: 'auto',
              border: '1px solid #007CDC',
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              {`Results: ${formatSecondsToDateTimeString(
                activeHomeworkContent.assessment.assessmentTargetDate?.seconds
              )}`}
            </Typography>
          </Box>
        )}
        <Box sx={styles.buttonBox}>
          {checkFeatureData({
            assignmentConfigInfo:
              activeHomeworkContent?.assessment?.assignmentInfo,
            userInfo: userInfo,
          }).reattempt ? (
            <PrimaryButton onClick={reattemptClickHandler}>
              <Typography variant="h3" color="common.white" fontWeight="bold">
                REATTEMPT
              </Typography>
            </PrimaryButton>
          ) : (
            <PrimaryButton onClick={() => navigate(HOMEWORK_STUDENT_DASHBOARD)}>
              <Typography variant="h3" color="common.white" fontWeight="bold">
                GO TO HOMEPAGE
              </Typography>
            </PrimaryButton>
          )}
        </Box>
      </Box>

      <Box sx={styles.sectionListBox}>
        {loading === true ? (
          <SectionListSckeleton fullWidth children={ResourceShimmerArray} />
        ) : (
          <FullWidthSectionList
            hideListCount
            isError={error}
            noContentMessage="No Active Assessments"
            sectionTitle={'Next Assessments'}
            items={nextHomeworks}
          />
        )}
        <HomeworkRecommendations
          subjectId={
            Number(activeHomeworkContent?.assessment?.subjectId) || undefined
          }
          chapterId={
            Number(activeHomeworkContent?.assessment?.chapterId) || undefined
          }
          topicId={
            Number(activeHomeworkContent?.assessment?.moduleId) || undefined
          }
        />
        {/* <FullWidthSectionList
          hideListCount
          sectionTitle="_Recommended Resources"
          items={hwData.active.map((elem, index) => (
            <ContentDetailCard
              variant="medium"
              image={'/assets/shared-ui/tempAssets/lessonImage1.png'}
              heading={"ChapterSummary"}
              iconDetails={[
                {
                  iconName: 'clock',
                  text: '15 Min',
                },
                {
                  iconName: 'questions',
                  text: 'Reading',
                },
              ]}
              rootStyle={{
                backgroundColor: 'common.white'
              }}
            />
          ))}
        /> */}
      </Box>
      <EarlyHomeworkPopup
        open={!!startTime}
        okHandler={() => {
          setStartTime(undefined);
          setAssessmentType(undefined);
        }}
        startTime={startTime}
      />
      <ClickerPoupSingleButton
        open={openClickerPopup}
        iconName={'clicker_icon'}
        handleClose={() => {
          setOpenClickerPopup(false);
        }}
        ctaHandler={() => {
          setOpenClickerPopup(false);
        }}
        buttontext="OK"
      />
    </Box>
  );
};
const ResourceShimmerArray = [
  <ShimmerActiveHwCard key={1} variant="large" />,
  <ShimmerActiveHwCard key={2} variant="large" />,
  <ShimmerActiveHwCard key={3} variant="large" />,
];
const hwData = {
  active: [
    {
      subject: 'Science',
      chapter: 'Friction',
      hwName: 'My Homework',
      totalQuestion: '20',
      timeLeft: '10 hrs',
      name: 'Deepali',
      completed: '0',
    },
    {
      subject: 'Science',
      chapter: 'Friction',
      hwName: 'My Homework',
      totalQuestion: '20',
      timeLeft: '10 hrs',
      name: 'Deepali',
      completed: '45',
    },
    {
      subject: 'Science',
      chapter: 'Friction',
      hwName: 'My Homework',
      totalQuestion: '20',
      timeLeft: '10 hrs',
      name: 'Deepali',
      completed: '100',
    },
    {
      subject: 'Science',
      chapter: 'Friction',
      hwName: 'My Homework',
      totalQuestion: '20',
      timeLeft: '10 hrs',
      name: 'Deepali',
      completed: '0',
    },
  ],
};
const checkSubjectiveQuestion = (question: Question) => {
  if (
    question?.questionType === Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT ||
    question?.questionType === Question_QuestionEnum.QUESTION_TYPE_SHORT ||
    question?.questionType === Question_QuestionEnum.QUESTION_TYPE_LONG ||
    question?.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
  ) {
    return true;
  } else {
    return false;
  }
};
